import { useEffect } from 'react'

interface CoverageBarProps {
  accepted: number
  declined: number
  waiting: number
  lesson_count?: number
}

function buildLabel(accepted: number, declined: number, waiting: number, total: number): string {
  if (accepted === total) {
    return `${accepted}/${total} Lektionen angenommen`
  }
  if (declined === total) {
    return `0/${total} Lektionen — alle abgelehnt`
  }
  const parts: string[] = []
  if (accepted > 0) {
    parts.push(`${accepted}/${total} Lektionen angenommen`)
  }
  if (waiting > 0) {
    parts.push(`${waiting} im Wartepool`)
  }
  if (declined > 0) {
    parts.push(`${declined} abgelehnt`)
  }
  if (parts.length > 0) {
    return parts.join(', ')
  }
  return `0/${total} Lektionen — wartet`
}

export function CoverageBar({ accepted, declined, waiting, lesson_count }: CoverageBarProps) {
  const calculatedTotal = accepted + declined + waiting
  const total = lesson_count ?? calculatedTotal

  useEffect(() => {
    if (import.meta.env.DEV && lesson_count !== undefined && calculatedTotal > lesson_count) {
      console.warn(
        `CoverageBar mismatch: calculated=${calculatedTotal}, lesson_count=${lesson_count}`,
        { accepted, declined, waiting }
      )
    }
  }, [accepted, declined, waiting, calculatedTotal, lesson_count])

  if (total === 0) {
    return (
      <div className="mb-2">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-gray-500">Coverage</span>
          <span className="text-gray-500 font-medium">Noch keine Antworten</span>
        </div>
        <div className="h-2 rounded bg-gray-200 overflow-hidden">
          <div className="h-full bg-gray-400" style={{ width: '0%' }} />
        </div>
      </div>
    )
  }

  const acceptedPct = (accepted / total) * 100
  const waitingPct = (waiting / total) * 100
  const declinedPct = (declined / total) * 100
  const label = buildLabel(accepted, declined, waiting, total)

  const labelColor = accepted === total
    ? 'text-green-700'
    : declined === total
      ? 'text-red-700'
      : waiting > 0
        ? 'text-blue-700'
        : 'text-gray-700'

  return (
    <div className="mb-2">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-500">Coverage</span>
        <span className={`${labelColor} font-medium`}>{label}</span>
      </div>
      <div className="h-2 rounded bg-gray-200 overflow-hidden flex">
        {accepted > 0 && (
          <div
            className="h-full bg-green-500"
            style={{ width: `${acceptedPct}%` }}
            title={`${accepted} Lektionen angenommen`}
          />
        )}
        {waiting > 0 && (
          <div
            className="h-full bg-blue-500"
            style={{ width: `${waitingPct}%` }}
            title={`${waiting} Lektionen im Wartepool`}
          />
        )}
        {declined > 0 && (
          <div
            className="h-full bg-red-500"
            style={{ width: `${declinedPct}%` }}
            title={`${declined} Lektionen abgelehnt`}
          />
        )}
      </div>
    </div>
  )
}
